import { InputAdornment } from '@mui/material';
import { E164Number } from 'libphonenumber-js/core';
import { AsYouType } from 'libphonenumber-js/min';
import * as React from 'react';
import PhoneInput from 'react-phone-number-input/input';

import { PhoneIcon } from '@/components/icons/PhoneIcon';

import { FormControl } from '../FormControl';
import {
  BasePhoneNumberInputProps,
  FormControlPhoneNumberInputProps,
  HelpTextVariant,
} from '../inputTypes';
import { BaseTextInput } from '../TextInput/TextInput';

interface PhoneNumberInputProps extends FormControlPhoneNumberInputProps {
  label: string;
  // the presence of contextualHelp indicates that there's help text related to this input, which
  // will cause the "info" icon to show up next to the input label
  contextualHelp?: JSX.Element;
  errorMessage?: string;
  helpText?: string;
  helpTextVariant?: HelpTextVariant;
  hideLabel?: boolean;
}

const PhoneNumberTextInput = React.forwardRef<
  HTMLInputElement,
  BasePhoneNumberInputProps & {
    onValueChange: (value?: E164Number) => void;
  }
>(function PhoneNumberTextInput(
  { onChange, onKeyDown, onValueChange, ...props },
  ref
) {
  const publishValue = (displayValue: string) => {
    // Use the same country and metadata as PhoneInput's formatter.
    const formatter = new AsYouType('US');
    formatter.input(displayValue);
    onValueChange(formatter.getNumberValue());
  };

  // MUI must check the formatted display value, not the E.164 value. These
  // can disagree about whether the input is empty for incomplete prefixes.
  return (
    <BaseTextInput
      {...props}
      inputRef={ref}
      onChange={(event) => {
        // The formatter updates the DOM and its state before it returns.
        // Update the form in the same event so a delayed effect cannot echo
        // an old value back into the formatter during rapid input.
        onChange?.(event);
        publishValue(event.target.value);
      }}
      inputProps={{
        onKeyDown: (event: React.KeyboardEvent<HTMLInputElement>) => {
          const input = event.currentTarget;
          const previousValue = input.value;
          onKeyDown?.(event);
          // Smart caret handles deletion itself and cancels the native edit,
          // so Backspace and Delete do not produce a change event.
          if (input.value !== previousValue) {
            publishValue(input.value);
          }
        },
      }}
    />
  );
});

function BasePhoneNumberInput({
  // see here: https://github.com/mui/material-ui/issues/12946
  // stripping off onKeyUp and onKeyDown is required here because TextField doesn't actually implement those props.
  onKeyUp: _onKeyUp,
  onKeyDown: _onKeyDown,
  startAdornment = (
    <InputAdornment position="start">
      <PhoneIcon size={16} />
    </InputAdornment>
  ),
  testId,
  inputRef,
  onChange,
  name,
  value,
  ...inputProps
}: BasePhoneNumberInputProps) {
  return (
    <PhoneInput
      {...inputProps}
      defaultCountry="US"
      // The library's default component type omits required props. FormControl
      // supplies the input ID, and PhoneInput forwards it to this adapter.
      inputComponent={
        PhoneNumberTextInput as NonNullable<
          React.ComponentProps<typeof PhoneInput>['inputComponent']
        >
      }
      ref={inputRef}
      name={name}
      type="text"
      // Phone fields store an E.164 string or an empty value.
      value={(value as string | undefined) || undefined}
      startAdornment={startAdornment}
      testId={testId ?? `phone-number-input-${name ?? ''}`}
      // The adapter publishes edits synchronously. Do not also publish the
      // library's effect-driven notifications, which can contain stale values.
      onChange={() => undefined}
      onValueChange={(nextValue?: E164Number) => {
        // Keep the event-shaped callback and empty-string form contract.
        onChange?.({
          target: { name, value: nextValue || '' },
        } as React.ChangeEvent<HTMLInputElement>);
      }}
    />
  );
}

export function PhoneNumberInput({
  label,
  contextualHelp,
  errorMessage,
  helpText,
  helpTextVariant,
  hideLabel,
  ...inputProps
}: PhoneNumberInputProps) {
  return (
    <FormControl<BasePhoneNumberInputProps>
      component={BasePhoneNumberInput}
      inputProps={inputProps}
      errorMessage={errorMessage}
      contextualHelp={contextualHelp}
      required={inputProps.required}
      helpText={helpText}
      helpTextVariant={helpTextVariant}
      id={inputProps.id}
      label={label}
      hideLabel={hideLabel}
    />
  );
}
